import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
  QueryKey,
  QueryClient,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { addToast } from "@heroui/react";
import { t } from "@lingui/core/macro";
import { describeErrorForLog } from "@/utils/sentry-error";
import { sentryLog } from "@/utils/sentry-utils";

type CustomQueryOptions<
  TQueryFnData,
  TError,
  TData,
  TQueryKey extends QueryKey,
> = UseQueryOptions<TQueryFnData, TError, TData, TQueryKey> & {
  displayNotification?: boolean; // Add the displayNotification option
};

type CustomQueryResult<TData, TError> = UseQueryResult<TData, TError> & {
  processedErrorMessage: string | null;
  processedErrorCode: string | null;
};

export function useCustomQuery<
  TQueryFnData = unknown,
  TError = AxiosError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: CustomQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
  queryClient?: QueryClient,
): CustomQueryResult<TData, TError> {
  const { displayNotification = false, ...queryOptions } = options;

  const queryResult = useQuery<TQueryFnData, TError, TData, TQueryKey>(
    {
      ...queryOptions,
    },
    queryClient,
  );

  if (
    queryResult.error instanceof AxiosError &&
    queryResult.error?.status === 498
  ) {
    addToast({
      title: t`Seja je potekla`,
      description: t`Seja je potekla, ponovno se prijavite`,
      color: "danger",
    });
    sentryLog.error(
      "Logout user - session timeout",
      describeErrorForLog(queryResult.error),
    );
    localStorage.removeItem("usr_token");
    window.location.reload();
  }

  // Process the error message
  const processedErrorMessage =
    queryResult.error instanceof AxiosError
      ? queryResult.error?.response?.data?.error
      : null;

  const processedErrorCode =
    queryResult.error instanceof AxiosError
      ? queryResult.error?.response?.data?.code
      : null;

  // Show the notification only if displayNotification is true and there's an error
  if (displayNotification && processedErrorMessage) {
    addToast({
      title: t`Napaka pri pridobivanju podatkov`,
      description:
        processedErrorMessage || t`Prišlo je do nepričakovane napake`,
      color: "danger",
    });
  }

  return {
    ...queryResult,
    processedErrorMessage,
    processedErrorCode,
  };
}
